/**
 * `kind: MULTI_SELECT` — any number of the question's options.
 *
 * ## No combination constraints yet, on purpose
 *
 * The live questionnaire does constrain these: on `Q1.1.3.1` some PPA answers cannot be
 * combined. But that rule is in NO data — not in `QuestionnaireNode`, not in the canvas
 * editor, not in the document — it is hardcoded in the old BD frontend, and inconsistently:
 * one question is validated by the label's leading letter and its structurally identical
 * sibling by `value`, which is why "R-PPA" can combine on one and not the other.
 *
 * So this control cannot enforce anything without inventing the rules. `selection` is the
 * prop it will take when the document gains the field; until then it is absent and every
 * combination is allowed. Enforcing a guess would be worse than enforcing nothing, because a
 * wrong constraint silently blocks a legitimate answer.
 */
import { Checkbox, SpaceBetween } from '@amzn/awsui-components-react';

import type { DocOption } from '../../utils/wire';
import type { AnswerControlProps } from '../answerTypes';

/** Reserved for the document field that does not exist yet. */
export interface SelectionConstraint {
  min?: number;
  max?: number;
  /** Option values that must be the only selection when chosen. */
  exclusive?: string[];
}

export interface MultiSelectAnswerProps extends AnswerControlProps {
  options: DocOption[];
  /** Absent today: see the note above. */
  selection?: SelectionConstraint;
}

export default function MultiSelectAnswer({
  options,
  value,
  onChange,
  readOnly,
}: MultiSelectAnswerProps) {
  const chosen = Array.isArray(value) ? value : [];

  const toggle = (optionValue: string, checked: boolean) => {
    const next = checked
      ? [...chosen, optionValue]
      : chosen.filter((v) => v !== optionValue);
    // Option order, not click order: the answer is a set, and storing it in the order the BD
    // happened to tick would make two identical answers compare unequal.
    const ordered = options.map((o) => o.optionValue).filter((v) => next.includes(v));
    onChange(ordered.length ? ordered : undefined);
  };

  return (
    <SpaceBetween size="xs">
      {options.map((o) => (
        <Checkbox
          key={o.optionValue}
          checked={chosen.includes(o.optionValue)}
          readOnly={readOnly}
          onChange={({ detail }) => toggle(o.optionValue, detail.checked)}
        >
          {o.label.en}
          {o.label.zh && <div className="cpa-a__zh">{o.label.zh}</div>}
        </Checkbox>
      ))}
    </SpaceBetween>
  );
}
